/**
 * Unified user initialization query
 *
 * Fetches all user-specific data needed on app load in a single API call.
 * This consolidates 10 separate API calls into 1:
 * - /profile/balance
 * - /profile
 * - /joining-bonus/status
 * - /daily-bonus/status
 * - /cms-pages/policies/pending
 * - /recently-played
 * - /site-config/public/social
 * - /help-center/contact-channels
 * - /cms-pages
 * - /payments/available-methods
 *
 * Individual contexts can subscribe to this data via the query cache
 * rather than making their own API calls.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { websocketClient } from '@/lib/websocket-client';
import { storage } from '@/lib/storage';
import type { SocialLinks } from './useSiteConfigQuery';
import type { HelpCenterContactChannel } from './useHelpCenterQuery';
import type { CMSPage } from './useCMSPagesQuery';

export interface UserBalance {
  gc_amount: number;
  sc_bonus_amount: number;
  sc_promotional_amount: number;
  sc_redeemable_amount: number;
}

export interface UserProfile {
  id: string;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  date_of_birth?: string;
  state?: string;
  address?: string;
  city?: string;
  zip_code?: string;
  email_verified?: boolean;
  profile_completed?: boolean;
  [key: string]: any;
}

export interface JoiningBonusStatus {
  bonus_claimed: boolean;
  bonus_amount_gc?: number;
  bonus_amount_sc?: number;
  claimed_at?: string;
}

export interface DailyBonusStatus {
  enabled: boolean;
  streak: {
    currentStreak: number;
    longestStreak: number;
    currentCycleDay: number;
    lastLoginDate?: string;
  };
  todayReward: {
    canClaim: boolean;
    gc: number;
    sc: number;
  };
  cycleProgress: Array<{
    day: number;
    gc: number;
    sc: number;
    status: 'claimed' | 'earned' | 'current' | 'upcoming' | 'expired';
  }>;
  vipTier: {
    id: number;
    name: string;
    multiplier: number;
  };
  disclosure: {
    amoeAvailable: boolean;
  };
}

export interface PendingPolicy {
  id: number;
  slug: string;
  title: string;
  pdf_url?: string;
  updated_at: string;
  requires_acceptance: boolean;
}

export interface RecentlyPlayedGame {
  game_id: string; // Prefixed external ID, e.g. "iconic21-dragons-fortune"
  game_title: string;
  game_image?: string;
  game_provider?: string;
  aggregator_name?: string;
  external_game_id?: string;
  last_played_at: string;
}

export interface AvailableMethodsData {
  methods: string[];
  methodProcessors: Record<string, string[]>;
  methodDetails: Record<string, {
    primaryProcessor: string;
    processors: Array<{ code: string; displayName: string; priority: number }>;
  }>;
  processors: Array<{ code: string; displayName: string; methods: string[] }>;
}

export interface UserInitData {
  balance: UserBalance | null;
  profile: UserProfile | null;
  joiningBonus: { success: boolean; status: JoiningBonusStatus } | null;
  dailyBonus: DailyBonusStatus | null;
  pendingPolicies: PendingPolicy[];
  recentlyPlayed: RecentlyPlayedGame[];
  socialLinks: SocialLinks | null;
  contactChannels: HelpCenterContactChannel[];
  cmsPages: CMSPage[];
  availableMethods: AvailableMethodsData | null;
}

const TEN_MINUTES = 10 * 60 * 1000;

/**
 * Unified user initialization query
 *
 * Fetches all user-specific data in a single API call.
 * Use this to pre-populate data for multiple contexts.
 */
export function useUserInitQuery() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Subscribe to WebSocket events that should invalidate init cache
  useEffect(() => {
    if (!user) return;

    const token = storage.getItem('auth_token') || storage.getItem('token');
    if (!token) return;

    websocketClient.connect(token).catch(() => {
      // Connection errors handled by websocket client
    });

    const handlePaymentMethodsUpdated = () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'init'] });
    };

    const unsubscribe = websocketClient.subscribe('payment:methods:updated', handlePaymentMethodsUpdated);

    return () => {
      unsubscribe();
    };
  }, [user, queryClient]);

  return useQuery({
    queryKey: ['user', 'init'],
    queryFn: async (): Promise<UserInitData> => {
      const response = await apiClient.get<{
        success: boolean;
        balance: UserBalance | null;
        profile: UserProfile | null;
        joiningBonus: { success: boolean; status: JoiningBonusStatus } | null;
        dailyBonus: DailyBonusStatus | null;
        pendingPolicies: PendingPolicy[];
        recentlyPlayed: RecentlyPlayedGame[];
        socialLinks: SocialLinks | null;
        contactChannels: HelpCenterContactChannel[];
        cmsPages: CMSPage[];
        availableMethods: AvailableMethodsData | null;
      }>('/user/init');

      return {
        balance: response?.balance || null,
        profile: response?.profile || null,
        joiningBonus: response?.joiningBonus || null,
        dailyBonus: response?.dailyBonus || null,
        pendingPolicies: response?.pendingPolicies || [],
        recentlyPlayed: response?.recentlyPlayed || [],
        socialLinks: response?.socialLinks || null,
        contactChannels: response?.contactChannels || [],
        cmsPages: response?.cmsPages || [],
        availableMethods: response?.availableMethods || null,
      };
    },
    staleTime: TEN_MINUTES,
    gcTime: TEN_MINUTES * 2,
    enabled: !!user,
  });
}

/**
 * Hook to get just the balance from the user init data
 */
export function useUserInitBalance() {
  const { data } = useUserInitQuery();
  return data?.balance || null;
}

/**
 * Hook to get just the profile from the user init data
 */
export function useUserInitProfile() {
  const { data } = useUserInitQuery();
  return data?.profile || null;
}

/**
 * Hook to get joining bonus status from the user init data
 */
export function useUserInitJoiningBonus() {
  const { data } = useUserInitQuery();
  return data?.joiningBonus || null;
}

/**
 * Hook to get daily bonus status from the user init data
 */
export function useUserInitDailyBonus() {
  const { data } = useUserInitQuery();
  return data?.dailyBonus || null;
}

/**
 * Hook to get pending policies from the user init data
 */
// Stable empty array references to prevent infinite re-render loops
// when these are used as useEffect dependencies in context providers
const EMPTY_POLICIES: PendingPolicy[] = [];
const EMPTY_RECENTLY_PLAYED: RecentlyPlayedGame[] = [];
const EMPTY_CHANNELS: HelpCenterContactChannel[] = [];
const EMPTY_CMS_PAGES: CMSPage[] = [];

export function useUserInitPendingPolicies() {
  const { data } = useUserInitQuery();
  return data?.pendingPolicies ?? EMPTY_POLICIES;
}

/**
 * Hook to get recently played games from the user init data
 */
export function useUserInitRecentlyPlayed() {
  const { data } = useUserInitQuery();
  return data?.recentlyPlayed ?? EMPTY_RECENTLY_PLAYED;
}

/**
 * Hook to get social links from the user init data
 */
export function useUserInitSocialLinks() {
  const { data } = useUserInitQuery();
  return data?.socialLinks || null;
}

/**
 * Hook to get contact channels from the user init data
 */
export function useUserInitContactChannels() {
  const { data } = useUserInitQuery();
  return data?.contactChannels ?? EMPTY_CHANNELS;
}

/**
 * Hook to get CMS pages from the user init data
 */
export function useUserInitCMSPages() {
  const { data } = useUserInitQuery();
  return data?.cmsPages ?? EMPTY_CMS_PAGES;
}

/**
 * Hook to get available payment methods from the user init data
 */
export function useUserInitAvailableMethods() {
  const { data } = useUserInitQuery();
  return data?.availableMethods || null;
}
